import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../interceptors";

const PROVIDERS = [
  {
    key: "GREENHOUSE",
    name: "Greenhouse",
    desc: "Full-cycle ATS with structured hiring & scorecards",
    authType: "OAuth 2.0",
    tag: "Most popular",
    tagColor: "bg-emerald-50 text-emerald-700",
    color: "#23A55A",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#ECFDF5" />
        <path d="M16 8c-1.5 3-5 5-5 9a5 5 0 0010 0c0-4-3.5-6-5-9z" fill="#23A55A" />
        <path d="M16 14c-.8 1.6-2.5 2.5-2.5 4.5a2.5 2.5 0 005 0c0-2-1.7-2.9-2.5-4.5z" fill="#16A34A" />
      </svg>
    ),
  },
  {
    key: "LEVER",
    name: "Lever",
    desc: "CRM + ATS for pipeline builders — candidate-centric model",
    authType: "OAuth 2.0",
    tag: "Popular",
    tagColor: "bg-blue-50 text-blue-700",
    color: "#3B82F6",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#EFF6FF" />
        <rect x="10" y="10" width="12" height="12" rx="3" fill="#3B82F6" />
        <text x="16" y="20" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">L</text>
      </svg>
    ),
  },
  {
    key: "ASHBY",
    name: "Ashby",
    desc: "All-in-one recruiting platform — fast-growing with startups",
    authType: "API Key",
    tag: "Startups",
    tagColor: "bg-purple-50 text-purple-700",
    color: "#7C3AED",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#F5F3FF" />
        <rect x="10" y="10" width="12" height="12" rx="3" fill="#7C3AED" />
        <text x="16" y="20" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">A</text>
      </svg>
    ),
  },
  {
    key: "WORKABLE",
    name: "Workable",
    desc: "Hiring suite for SMBs — job boards, sourcing, and evaluations",
    authType: "OAuth 2.0",
    tag: "SMB",
    tagColor: "bg-sky-50 text-sky-700",
    color: "#0EA5E9",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#F0F9FF" />
        <rect x="10" y="10" width="12" height="12" rx="3" fill="#0EA5E9" />
        <text x="16" y="20" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">W</text>
      </svg>
    ),
  },
  {
    key: "BAMBOOHR",
    name: "BambooHR",
    desc: "HRIS + ATS — employee data, onboarding, and applicant tracking",
    authType: "API Key",
    tag: "HR + ATS",
    tagColor: "bg-lime-50 text-lime-700",
    color: "#65A30D",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#F7FEE7" />
        <path d="M14 8v16M18 10v12M11 12v8M21 14v4" stroke="#65A30D" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "ICIMS",
    name: "iCIMS",
    desc: "Enterprise talent cloud — complex, per-customer schema",
    authType: "OAuth 2.0",
    tag: "Enterprise",
    tagColor: "bg-amber-50 text-amber-700",
    color: "#D97706",
    logo: (
      <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
        <circle cx="16" cy="16" r="15" fill="#FFFBEB" />
        <rect x="10" y="10" width="12" height="12" rx="3" fill="#D97706" />
        <text x="16" y="20" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">iC</text>
      </svg>
    ),
  },
];

const AMPLE_STAGES = [
  "Invited",
  "Confirmed",
  "In Progress",
  "Scored",
  "Shortlisted",
  "Rejected",
  "Hired",
];

const SYNC_INTERVALS = [
  { value: 5, label: "Every 5 min" },
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Every hour" },
  { value: 0, label: "Manual only" },
];

export default function Integrations() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [entityMap, setEntityMap] = useState([]);
  const [atsStages, setAtsStages] = useState([]);
  const [stageMapping, setStageMapping] = useState({});
  const [apiKeyModal, setApiKeyModal] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [subdomainInput, setSubdomainInput] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(null);

  useEffect(() => {
    document.title = "Integrations - Ample Hire";
    loadConnections();
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadConnections = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get("/common/integrations/ats/connections");
      setConnections(r.data || []);
    } catch {
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  const getConnection = (providerKey) =>
    connections.find((c) => c.provider === providerKey);

  const handleConnect = async (provider) => {
    const meta = PROVIDERS.find((p) => p.key === provider);
    if (meta.authType === "API Key") {
      setApiKeyModal(provider);
      return;
    }
    setActionLoading(provider);
    try {
      let conn = getConnection(provider);
      if (!conn) {
        const r = await axiosInstance.post(
          "/common/integrations/ats/connections",
          {
            provider,
            displayName: `${meta.name} Integration`,
            tenantId: Number(localStorage.getItem("organizationId")) || 1,
          }
        );
        conn = r.data;
        await loadConnections();
      }
      const r = await axiosInstance.get(
        `/common/integrations/ats/connections/${conn.id}/auth-url`
      );
      window.open(r.data?.url || r.data, "_blank", "width=600,height=700");
    } catch (e) {
      showToast(e?.message || "Failed to start OAuth flow", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleApiKeySave = async () => {
    setActionLoading(apiKeyModal);
    try {
      const meta = PROVIDERS.find((p) => p.key === apiKeyModal);
      let conn = getConnection(apiKeyModal);
      if (!conn) {
        const r = await axiosInstance.post(
          "/common/integrations/ats/connections",
          {
            provider: apiKeyModal,
            displayName: `${meta.name} Integration`,
            tenantId: Number(localStorage.getItem("organizationId")) || 1,
          }
        );
        conn = r.data;
      }
      await axiosInstance.post(
        `/common/integrations/ats/connections/${conn.id}/api-key`,
        { apiKey: apiKeyInput, subdomain: subdomainInput }
      );
      showToast(`${meta.name} connected successfully`);
      setApiKeyModal(null);
      setApiKeyInput("");
      setSubdomainInput("");
      await loadConnections();
    } catch (e) {
      showToast(e?.message || "Failed to save API key", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (connId) => {
    setActionLoading("disconnect");
    try {
      await axiosInstance.delete(
        `/common/integrations/ats/connections/${connId}`
      );
      showToast("Disconnected successfully");
      setExpandedProvider(null);
      setDisconnectConfirm(null);
      await loadConnections();
    } catch (e) {
      showToast(e?.message || "Failed to disconnect", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncNow = async (connId) => {
    setActionLoading("sync");
    try {
      await axiosInstance.post(
        `/common/integrations/ats/connections/${connId}/sync`
      );
      showToast("Sync started");
      await loadConnections();
    } catch (e) {
      showToast(e?.message || "Sync failed", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleTestConnection = async (connId) => {
    setActionLoading("test");
    try {
      const r = await axiosInstance.post(
        `/common/integrations/ats/connections/${connId}/test`
      );
      showToast(r.data?.message || "Connection healthy");
    } catch (e) {
      showToast(e?.message || "Connection test failed", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const loadSyncLogs = async (connId) => {
    try {
      const r = await axiosInstance.get(
        `/common/integrations/ats/connections/${connId}/sync-logs?size=20`
      );
      setSyncLogs(r.data?.content || r.data || []);
    } catch {
      setSyncLogs([]);
    }
  };

  const loadEntityMap = async (connId) => {
    try {
      const r = await axiosInstance.get(
        `/common/integrations/ats/connections/${connId}/entity-map?size=20`
      );
      setEntityMap(r.data?.content || r.data || []);
    } catch {
      setEntityMap([]);
    }
  };

  const openProviderDetail = useCallback(
    (providerKey) => {
      if (expandedProvider === providerKey) {
        setExpandedProvider(null);
        return;
      }
      setExpandedProvider(providerKey);
      const conn = getConnection(providerKey);
      if (conn) {
        setStageMapping(
          conn.stageMapping ? JSON.parse(conn.stageMapping || "{}") : {}
        );
        loadSyncLogs(conn.id);
        loadEntityMap(conn.id);
      }
    },
    [expandedProvider, connections]
  );

  const handleSaveSyncSettings = async (conn, interval) => {
    try {
      await axiosInstance.put(
        `/common/integrations/ats/connections/${conn.id}`,
        {
          syncIntervalMinutes: interval,
          stageMapping: JSON.stringify(stageMapping),
        }
      );
      showToast("Settings saved");
      await loadConnections();
    } catch (e) {
      showToast(e?.message || "Failed to save", "error");
    }
  };

  const relTime = (iso) => {
    if (!iso) return "Never";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "Just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Integrations</h1>
          <p className="text-sm text-vf-muted mt-1">
            Connect your ATS to sync jobs, candidates, and push AI assessment
            results back automatically.
          </p>
        </div>
        <button
          onClick={loadConnections}
          className="px-3 py-1.5 rounded border border-slate-300 text-xs"
        >
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-vf-muted py-16">
          Loading integrations…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROVIDERS.map((p) => {
            const conn = getConnection(p.key);
            const isConnected = conn?.status === "CONNECTED";
            const isSyncing = conn?.status === "SYNCING";
            const isError = conn?.status === "ERROR";
            const isExpanded = expandedProvider === p.key;

            return (
              <div
                key={p.key}
                className={`bg-white border rounded-xl transition-all ${isExpanded ? "lg:col-span-3 border-vf-highlight shadow-md" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">{p.logo}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-vf-base">
                          {p.name}
                        </h3>
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${p.tagColor}`}
                        >
                          {p.tag}
                        </span>
                      </div>
                      <p className="text-xs text-vf-muted mb-2">{p.desc}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-vf-muted bg-slate-50 px-1.5 py-0.5 rounded">
                          {p.authType}
                        </span>
                        <span className="flex items-center gap-1 text-[11px]">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-emerald-500" : isError ? "bg-red-500" : isSyncing ? "bg-amber-500" : "bg-slate-300"}`}
                          />
                          <span
                            className={
                              isConnected
                                ? "text-emerald-600"
                                : isError
                                  ? "text-red-600"
                                  : isSyncing
                                    ? "text-amber-600"
                                    : "text-vf-muted"
                            }
                          >
                            {conn?.status || "Not connected"}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isConnected || isError || isSyncing ? (
                        <button
                          onClick={() => openProviderDetail(p.key)}
                          className="px-3 py-1.5 rounded border border-slate-300 text-xs hover:bg-vf-tint"
                        >
                          {isExpanded ? "Close" : "Settings"}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnect(p.key)}
                          disabled={actionLoading === p.key}
                          className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium hover:bg-vf-primary disabled:opacity-50"
                        >
                          {actionLoading === p.key
                            ? "Connecting…"
                            : "Connect"}
                        </button>
                      )}
                    </div>
                  </div>

                  {isConnected && !isExpanded && conn.lastSyncAt && (
                    <div className="mt-3 text-[11px] text-vf-muted border-t border-slate-100 pt-2">
                      Last sync: {relTime(conn.lastSyncAt)} ·{" "}
                      {conn.lastSyncStatus || "—"}
                    </div>
                  )}
                </div>

                {/* ── Expanded Detail View ── */}
                {isExpanded && conn && (
                  <div className="border-t border-slate-200 p-5 space-y-6">
                    {/* Status + Actions */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <button
                        onClick={() => handleTestConnection(conn.id)}
                        disabled={actionLoading === "test"}
                        className="px-3 py-1.5 rounded border border-slate-300 text-xs disabled:opacity-50"
                      >
                        {actionLoading === "test" ? "Testing…" : "Test connection"}
                      </button>
                      <button
                        onClick={() => handleSyncNow(conn.id)}
                        disabled={actionLoading === "sync" || isSyncing}
                        className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs disabled:opacity-50"
                      >
                        {actionLoading === "sync" || isSyncing
                          ? "Syncing…"
                          : "Sync now"}
                      </button>
                      <div className="ml-auto flex items-center gap-3">
                        <label className="text-xs text-vf-muted flex items-center gap-2">
                          Interval:
                          <select
                            value={conn.syncIntervalMinutes ?? 15}
                            onChange={(e) =>
                              handleSaveSyncSettings(
                                conn,
                                Number(e.target.value)
                              )
                            }
                            className="border border-slate-200 rounded px-2 py-1 text-xs"
                          >
                            {SYNC_INTERVALS.map((i) => (
                              <option key={i.value} value={i.value}>
                                {i.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          onClick={() => setDisconnectConfirm(conn.id)}
                          className="px-3 py-1.5 rounded border border-red-300 text-red-600 text-xs hover:bg-red-50"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>

                    {isError && conn.lastSyncError && (
                      <div className="bg-red-50 text-red-700 text-xs rounded px-3 py-2">
                        Error: {conn.lastSyncError}
                      </div>
                    )}

                    {/* Stage Mapping */}
                    <div>
                      <h4 className="text-xs font-semibold text-vf-base uppercase tracking-wider mb-2">
                        Stage mapping
                      </h4>
                      <div className="grid grid-cols-2 gap-2 max-w-md">
                        {AMPLE_STAGES.map((stage) => (
                          <React.Fragment key={stage}>
                            <span className="text-xs text-vf-base py-1">
                              {stage}
                            </span>
                            <input
                              type="text"
                              value={stageMapping[stage] || ""}
                              onChange={(e) =>
                                setStageMapping((m) => ({
                                  ...m,
                                  [stage]: e.target.value,
                                }))
                              }
                              placeholder={`${p.name} stage…`}
                              className="text-xs border border-slate-200 rounded px-2 py-1"
                            />
                          </React.Fragment>
                        ))}
                        <div className="col-span-2">
                          <button
                            onClick={() =>
                              handleSaveSyncSettings(
                                conn,
                                conn.syncIntervalMinutes
                              )
                            }
                            className="px-3 py-1 rounded bg-vf-primary text-white text-xs mt-1"
                          >
                            Save mapping
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Sync History */}
                    <div>
                      <h4 className="text-xs font-semibold text-vf-base uppercase tracking-wider mb-2">
                        Sync history
                      </h4>
                      {syncLogs.length === 0 ? (
                        <div className="text-xs text-vf-muted py-4 text-center border border-dashed border-slate-200 rounded">
                          No sync history yet.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-vf-muted uppercase">
                                <th className="py-1.5 pr-3">Time</th>
                                <th className="py-1.5 pr-3">Type</th>
                                <th className="py-1.5 pr-3">Direction</th>
                                <th className="py-1.5 pr-3">Jobs</th>
                                <th className="py-1.5 pr-3">Candidates</th>
                                <th className="py-1.5 pr-3">Scores</th>
                                <th className="py-1.5 pr-3">Status</th>
                                <th className="py-1.5">Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {syncLogs.map((log) => (
                                <tr
                                  key={log.id}
                                  className="border-t border-slate-100"
                                >
                                  <td className="py-1.5 pr-3 text-vf-muted">
                                    {relTime(log.startedAt)}
                                  </td>
                                  <td className="py-1.5 pr-3">{log.syncType}</td>
                                  <td className="py-1.5 pr-3">{log.direction}</td>
                                  <td className="py-1.5 pr-3">
                                    {log.jobsSynced}
                                  </td>
                                  <td className="py-1.5 pr-3">
                                    {log.candidatesSynced}
                                  </td>
                                  <td className="py-1.5 pr-3">
                                    {log.scoresPushed}
                                  </td>
                                  <td className="py-1.5 pr-3">
                                    <span
                                      className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${log.status === "SUCCESS" ? "bg-emerald-50 text-emerald-700" : log.status === "FAILED" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}
                                    >
                                      {log.status}
                                    </span>
                                  </td>
                                  <td className="py-1.5 text-vf-muted">
                                    {log.durationMs
                                      ? `${(log.durationMs / 1000).toFixed(1)}s`
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Entity Map */}
                    <div>
                      <h4 className="text-xs font-semibold text-vf-base uppercase tracking-wider mb-2">
                        Mapped entities
                      </h4>
                      {entityMap.length === 0 ? (
                        <div className="text-xs text-vf-muted py-4 text-center border border-dashed border-slate-200 rounded">
                          No entities mapped yet. Run a sync first.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-vf-muted uppercase">
                                <th className="py-1.5 pr-3">Type</th>
                                <th className="py-1.5 pr-3">Ample Hire ID</th>
                                <th className="py-1.5 pr-3">External ID</th>
                                <th className="py-1.5 pr-3">Last synced</th>
                                <th className="py-1.5">Link</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entityMap.map((e) => (
                                <tr
                                  key={e.id}
                                  className="border-t border-slate-100"
                                >
                                  <td className="py-1.5 pr-3">
                                    <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px]">
                                      {e.entityType}
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-3">{e.ampleId}</td>
                                  <td className="py-1.5 pr-3 font-mono">
                                    {e.externalId}
                                  </td>
                                  <td className="py-1.5 pr-3 text-vf-muted">
                                    {relTime(e.lastSyncedAt)}
                                  </td>
                                  <td className="py-1.5">
                                    {e.externalUrl ? (
                                      <a
                                        href={e.externalUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-vf-primary hover:underline"
                                      >
                                        Open ↗
                                      </a>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* API Key Modal */}
      {apiKeyModal && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
          onClick={() => setApiKeyModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-vf-base mb-1">
              Connect {PROVIDERS.find((p) => p.key === apiKeyModal)?.name}
            </h2>
            <p className="text-xs text-vf-muted mb-4">
              Enter your API key and subdomain to connect.
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
                  API Key
                </span>
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Paste your API key here"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-accent"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
                  Subdomain
                </span>
                <input
                  type="text"
                  value={subdomainInput}
                  onChange={(e) => setSubdomainInput(e.target.value)}
                  placeholder={
                    apiKeyModal === "BAMBOOHR"
                      ? "e.g. acme (from acme.bamboohr.com)"
                      : "e.g. acme"
                  }
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-accent"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setApiKeyModal(null)}
                className="px-3 py-1.5 rounded border border-slate-300 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleApiKeySave}
                disabled={!apiKeyInput || actionLoading === apiKeyModal}
                className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
              >
                {actionLoading === apiKeyModal
                  ? "Connecting…"
                  : "Connect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirm Modal */}
      {disconnectConfirm && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
          onClick={() => setDisconnectConfirm(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-xl mx-auto mb-3">
              ✖
            </div>
            <h3 className="text-base font-medium text-vf-base mb-2">
              Disconnect this integration?
            </h3>
            <p className="text-sm text-vf-muted mb-4">
              All tokens and sync data will be removed. You can reconnect
              later.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setDisconnectConfirm(null)}
                className="px-3 py-1.5 rounded border border-slate-300 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDisconnect(disconnectConfirm)}
                disabled={actionLoading === "disconnect"}
                className="px-3 py-1.5 rounded bg-red-500 text-white text-xs font-medium disabled:opacity-50"
              >
                {actionLoading === "disconnect"
                  ? "Disconnecting…"
                  : "Disconnect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[9999] px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${toast.type === "error" ? "bg-red-600 text-white" : "bg-vf-base text-white"}`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
